import React from "react";
import { createPortal } from "react-dom";

const TOAST_BOTTOM_OFFSET =
  "calc(var(--toast-bottom-clearance, max(env(safe-area-inset-bottom, 0px), 22px)) - 10px)";
const TOAST_Z_INDEX = 2147483000;

/**
 * Shared visual shell for transient platform notifications.
 *
 * Data sources (mailbox, assistant, chats) keep ownership of their lifecycle,
 * unread state and navigation. This component only owns presentation and
 * placement so the same notification contract can be reused across them.
 */
export default function PlatformNotificationToast({
  badge,
  title,
  message,
  openAriaLabel = "Open notification",
  closeAriaLabel = "Dismiss notification",
  onOpen,
  onDismiss,
  portalRoot,
  scoped = false,
}) {
  const workspaceScoped = Boolean(
    scoped || (portalRoot && portalRoot.id === "modal-root")
  );

  const stopPointerPropagation = (event) => {
    event.stopPropagation();
  };

  const handleOpen = () => {
    onOpen?.();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleOpen();
  };

  const content = (
    <div
      className={`${workspaceScoped ? "absolute" : "fixed"} -translate-x-1/2 pointer-events-auto`}
      style={{
        bottom: TOAST_BOTTOM_OFFSET,
        left: "50%",
        width: "min(calc(100vw - (var(--vg-page-gutter, 20px) * 2)), 640px)",
        maxWidth: workspaceScoped ? "calc(100% - 40px)" : undefined,
        zIndex: TOAST_Z_INDEX,
      }}
      role="status"
      aria-live="polite"
    >
      <div
        className="w-full cursor-pointer rounded-[24px] border border-white/[0.14] bg-[#3A4450]/[0.76] px-5 py-4 shadow-[0_14px_30px_rgba(2,7,18,0.28),inset_0_1px_0_rgba(255,255,255,0.07)] backdrop-blur-[18px] transition-colors hover:bg-[#46515e]/[0.82] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80"
        onMouseDown={stopPointerPropagation}
        onPointerDown={stopPointerPropagation}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label={openAriaLabel}
      >
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2 pr-8">
            <span
              className="rounded-full border border-white/70 bg-transparent px-2.5 py-1 text-[13px] font-semibold italic leading-tight text-white/78"
              style={{
                flex: "0 0 auto",
                minWidth: "max-content",
                whiteSpace: "nowrap",
                overflowWrap: "normal",
                wordBreak: "normal",
              }}
            >
              {badge}
            </span>
            <span
              className="text-[17px] font-extrabold leading-tight text-white"
              style={{
                flex: "1 1 0%",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </span>
          </div>

          <p className="mt-1 min-w-0 truncate text-[13px] font-medium leading-snug text-white/88">
            {message}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDismiss?.();
        }}
        aria-label={closeAriaLabel}
        className="absolute -right-2.5 -top-2.5 flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.24] bg-[#3A4450] text-white shadow-[0_8px_18px_rgba(2,7,18,0.28),inset_0_1px_0_rgba(255,255,255,0.12)] transition hover:bg-[#46515e] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[22px] w-[22px]" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
          <path d="m7 7 10 10M17 7 7 17" />
        </svg>
      </button>
    </div>
  );

  return portalRoot ? createPortal(content, portalRoot) : content;
}
